import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import HeroCarousel from "./components/HeroCarousel";
import CategoryQuick from "./components/CategoryQuick";
import RankingSidebar from "./components/RankingSidebar";
import FavoritesStrip from "./components/FavoritesStrip";
import AnimeCard from "@/components/feature/AnimeCard";
import LoadingState from "@/components/base/LoadingState";
import EmptyState from "@/components/base/EmptyState";
import { animeList } from "@/mocks/anime";

export default function Home() {
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 400);
    return () => clearTimeout(timer);
  }, []);

  const featured = useMemo(() => animeList.filter((a) => a.banner).slice(0, 4), []);
  const latest = useMemo(
    () => [...animeList].sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1)).slice(0, 8),
    []
  );
  const ranking = useMemo(
    () => [...animeList].sort((a, b) => b.views - a.views).slice(0, 8),
    []
  );

  return (
    <div className="w-full">
      <div className="w-full px-4 md:px-8 pt-6">
        <HeroCarousel items={featured} />
      </div>

      <div className="w-full px-4 md:px-8 py-10 md:py-14">
        <CategoryQuick />
      </div>

      <div className="w-full px-4 md:px-8">
        <FavoritesStrip />
      </div>

      <div className="w-full bg-background-100/60 py-10 md:py-14">
        <div className="w-full px-4 md:px-8">
          {loading ? (
            <LoadingState label="正在加载最新动漫…" />
          ) : latest.length === 0 ? (
            <EmptyState
              title="暂无推荐内容"
              description="内容正在更新中，请稍后刷新页面再试。"
            />
          ) : (
            <div className="flex flex-col lg:flex-row gap-8 lg:gap-10">
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-5">
                  <div>
                    <h2 className="text-xl md:text-2xl font-bold text-foreground-950">最新更新动漫</h2>
                    <p className="text-sm text-foreground-600 mt-1">按更新时间排序，追番不打烊</p>
                  </div>
                  <Link
                    to="/category"
                    className="text-sm text-primary-700 hover:text-primary-800 font-medium flex items-center gap-1 cursor-pointer whitespace-nowrap"
                  >
                    更多动漫
                    <i className="ri-arrow-right-s-line"></i>
                  </Link>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-5">
                  {latest.map((anime) => (
                    <AnimeCard key={anime.id} anime={anime} />
                  ))}
                </div>
              </div>
              <RankingSidebar items={ranking} />
            </div>
          )}
        </div>
      </div>

      <div className="w-full px-4 md:px-8 py-12 md:py-16">
        <div className="flex flex-col lg:flex-row items-center gap-8 lg:gap-14">
          <div className="w-full lg:w-1/2 rounded-lg overflow-hidden bg-background-100 aspect-[16/10]">
            <img
              src="https://readdy.ai/api/search-image?query=Stylized%20cozy%20anime%20illustration%20of%20a%20girl%20watching%20anime%20on%20a%20tablet%20surrounded%20by%20sakura%20petals%20and%20snacks%2C%20soft%20pink%20and%20cream%20palette%2C%20clean%20minimal%20gradient%20background%2C%20warm%20inviting%20lighting%2C%20detailed%20digital%20painting%2C%20no%20text&width=1200&height=750&seq=home-intro-01&orientation=landscape"
              alt="樱花动漫在线 追番体验展示"
              title="樱花动漫在线 动漫在线观看体验"
              className="w-full h-full object-cover object-top"
            />
          </div>
          <div className="w-full lg:w-1/2">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium mb-4">
              <i className="ri-flower-fill"></i>
              关于樱花动漫在线
            </span>
            <h2 className="text-2xl md:text-4xl font-bold text-foreground-950 mb-4 leading-snug">
              为每一位动漫爱好者
              <br className="hidden md:block" />
              准备的二次元内容门户
            </h2>
            <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-6">
              我们聚合了最新热播新番与经典动画，通过分类筛选、关键词搜索与标签推荐，
              让你用最短的时间找到最想看的那一部。支持收藏、专题合集与在线播放体验演示，
              无论是追番党还是补番党，都能在这里找到属于自己的那份心动。
            </p>
            <div className="grid grid-cols-3 gap-4 mb-8">
              {[
                { num: "1600+", label: "收录动漫" },
                { num: "11", label: "细分类型" },
                { num: "每日", label: "持续更新" },
              ].map((s) => (
                <div key={s.label} className="rounded-lg bg-background-100 px-4 py-3">
                  <p className="text-xl md:text-2xl font-bold text-primary-600">{s.num}</p>
                  <p className="text-xs text-foreground-600 mt-1">{s.label}</p>
                </div>
              ))}
            </div>
            <div className="flex flex-wrap gap-3">
              <Link
                to="/category"
                className="inline-flex items-center gap-2 px-6 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-apps-2-line"></i>
                浏览全部动漫
              </Link>
              <Link
                to="/topic"
                className="inline-flex items-center gap-2 px-6 py-2.5 rounded-md bg-secondary-100 text-secondary-900 text-sm font-semibold hover:bg-secondary-200 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-bookmark-3-line"></i>
                查看专题合集
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}